import { Component, ElementRef, input, InputSignal, OnDestroy, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-audio-player',
  imports: [
    MatIcon,
    TranslatePipe
  ],
  templateUrl: './audio-player.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './audio-player.component.scss'
})
export class AudioPlayerComponent implements OnDestroy {

  audioRef = viewChild.required<ElementRef<HTMLAudioElement>>('audio');

  isPlaying = signal(false);
  currentTime = signal(0);     // valor mostrado (interpolado)
  duration = signal(0);        // duração total
  private targetTime = 0;      // valor real do áudio (sincronizado 1x/s)
  private animationFrameId: number | null = null;
  private lastUpdate = 0;

  audioSrc: InputSignal<string> = input<string>('');
  title: InputSignal<string> = input<string>('');

  ngOnDestroy() {
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
  }

  togglePlay() {
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    
    const audio = this.audioRef().nativeElement;

    if (this.isPlaying()) {
      audio.pause();
      this.isPlaying.set(false);
    } else {
      audio.play().then(() => {
        this.isPlaying.set(true);
        this.animateProgress();
      });
    }
  }

  onLoadedMetadata() {
    this.duration.set(this.audioRef().nativeElement.duration);
  }

  onSeek(event: Event) {
    const audio = this.audioRef().nativeElement;
    const target = event.target as HTMLInputElement;
    const newTime = Number(target.value);
    audio.currentTime = newTime;
    this.targetTime = newTime;
    this.currentTime.set(newTime);
  }

  onEnded() {
    this.isPlaying.set(false);
    this.currentTime.set(0);
    this.targetTime = 0;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  animateProgress() {
    const audio = this.audioRef().nativeElement;
    let lastFrame = performance.now();

    const update = (now: number) => {
      const delta = now - lastFrame;
      lastFrame = now;
      this.lastUpdate += delta;
      // Atualiza o targetTime a cada 1 segundo (para estabilidade)
      if (this.lastUpdate >= 1000 && this.isPlaying()) {
        this.targetTime = audio.currentTime;
        this.lastUpdate = 0;
      }

      // Interpola suavemente o currentTime até o targetTime
      const displayed = this.currentTime();
      const lerpFactor = 0.01; // 0.1 = suavização moderada (quanto menor, mais suave)
      const newDisplayed = displayed + (this.targetTime - displayed) * lerpFactor;
      this.currentTime.set(newDisplayed);

      if (this.isPlaying()) {
        this.animationFrameId = requestAnimationFrame(update);
      }
    };

    this.animationFrameId = requestAnimationFrame(update);
  }

  formatTime(seconds: number): string {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  }

}
